import React, { ReactNode } from "react";
import { Link as RRLink } from "react-router-dom";
import {
  Card as RSCard,
  CardText,
  CardBody,
  CardTitle,
  CardSubtitle as RSCardSubtitle,
  Button,
} from "reactstrap";
import styled from "styled-components";
import urls from "../urls";

import { Col } from "../util/styledBootstrapGridWrapper";
import { FeedbackForm } from "./FeedbackForm";
import Fretboard from "./Fretboard";
import { SingleRow, TopRow } from "./Layout";
import Staff from "./staff/Staff";
import { H1 } from "./Typography";

const CardSubtitle = styled(RSCardSubtitle)`
  color: #6c757d;
`;

const Link = styled(RRLink)`
  &,
  &:hover {
    color: inherit;
    text-decoration: inherit;
  }
`;

const Card = styled(RSCard)`
  margin-top: 0;
  margin-bottom: 2rem;
  height: 100%;
  background-color: #fcfcfc;
  border-radius: 1.5rem;
  & svg {
    // limit the size of the fretboard on mobile & override max-height of Staff
    max-height: 77px !important;
    width: 100%;
  }
  &:hover {
    background-color: #f4f8fb;
    position: relative;
    top: -1px;
    box-shadow: 0 0.3rem 0.35rem rgba(0, 0, 0, 0.075) !important;
  }
`;

const CardHeader = styled.div`
  font-family: "Exo 2", sans-serif !important;
  font-size: 2.5rem;
  line-height: 77px; // match the fretboard height
  text-align: center;
`;

const FloatRightButton = styled(Button)`
  float: right;
`;

type Category = "Chords" | "Ear Training" | "Guitar" | "Intervals" | "Notation";

interface DrillCardProps {
  header: ReactNode;
  title: string;
  url: string;
  category: Category | Category[];
  description: string;
}

function DrillCard(props: DrillCardProps) {
  const Header =
    typeof props.header === "string" ? (
      <CardHeader>{props.header}</CardHeader>
    ) : (
      props.header
    );
  const categoryText =
    props.category instanceof Array
      ? props.category.join(" + ")
      : props.category;
  /* eslint-disable react/forbid-component-props */
  return (
    <Col lg={4} style={{ marginBottom: "2rem" }}>
      <Link to={props.url}>
        <Card className="shadow-sm">
          <CardBody>{Header}</CardBody>
          <CardBody>
            <CardTitle tag="h5">{props.title}</CardTitle>
            <CardSubtitle tag="h6">{categoryText}</CardSubtitle>
            <CardText>{props.description}</CardText>
            <FloatRightButton color="primary">
              {"Go to this drill!"}
            </FloatRightButton>
          </CardBody>
        </Card>
      </Link>
    </Col>
  );
  /* eslint-enable react/forbid-component-props */
}

const Jumbotron = styled.div`
  padding: 2rem 1rem;
  border-radius: 1.5rem;
  margin-bottom: 0;
  background-color: #f4f8fb;
`;

const HeroHeader = styled(H1)`
  font-size: 1.5rem;
  @media (min-width: 576px) {
    font-size: 2rem;
  }
  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
  @media (min-width: 992px) {
    font-size: 3.5rem;
  }
  font-weight: 300;
  line-height: 1.2;
  margin-bottom: 1rem;
  max-width: 20em;
`;

const HeroStaffWrapper = styled.div`
  width: 30%;
  float: right;
`;

/* eslint-disable react/forbid-component-props */
const Hero = () => (
  <Jumbotron className="shadow-sm">
    <HeroStaffWrapper>
      <Staff
        notes={[{ name: "E4" }, { name: "G4" }, { name: "C5" }, { name: "A4" }]}
        verticalMargin={450}
      />
    </HeroStaffWrapper>
    <HeroHeader>{"Music\u00a0Training That\u00a0Makes\u00a0Sense"}</HeroHeader>
    {/* eslint-disable react/jsx-no-literals */}
    <p className="lead">
      MusicDrill.com offers simple and effective drills to develop
      {"\u00a0"}your{"\u00a0"}musicality.
    </p>
    <p>
      The drills are especially designed to provide you the essential
      proficiency in areas like <em>interval{"\u00a0"}recognition</em>,{" "}
      <em>chord{"\u00a0"}knowledge</em>, <em>musical{"\u00a0"}notation</em>, and{" "}
      <em>ear{"\u00a0"}training</em>. The goal is to provide you relevant and
      convenient exercises to help you become the best possible musician you can
      be.
    </p>
    {/* eslint-enable react/jsx-no-literals */}
  </Jumbotron>
);
/* eslint-enable react/forbid-component-props */

export default function Frontpage() {
  return (
    <>
      <TopRow>
        <Col>
          <Hero />
        </Col>
      </TopRow>
      <SingleRow>
        <DrillCard
          header="I IV vi V"
          title="Chord Progression"
          url={urls.chordProgressionDrill}
          category={["Ear Training", "Chords"]}
          description="Learn to recognize different chord progressions by ear."
        />
        <DrillCard
          header="DO RE MI FA"
          title="Contextual Scale Degrees"
          url={urls.contextual}
          category="Ear Training"
          description="Learn to recognize scale degrees in musical context."
        />
        <DrillCard
          header={
            <Staff
              notes={[
                { name: "C4" },
                { name: "E4" },
                { name: "G4" },
                { name: "C5" },
              ]}
              verticalMargin={450}
            />
          }
          title="Melodic Dictation"
          url={urls.melodicDictation}
          category={["Ear Training", "Notation"]}
          description="Combined ear training and notation drill."
        />
        <DrillCard
          header="C - E - G"
          title="Chord Construction"
          url={urls.chordConstruction}
          category="Chords"
          description="You will learn how chords are constructed."
        />
        <DrillCard
          header="G + major 3rd"
          title="Interval Training"
          url={urls.intervalTraining}
          category="Intervals"
          description="Memorize intervals between notes so that you know them instantly without thinking."
        />
        <DrillCard
          header={<Fretboard notes={[{ string: 5, fret: 3, label: "C" }]} />}
          title="Fretboard Notes"
          url={urls.fretboardNotes}
          category="Guitar"
          description="Master the fretboard by memorizing all the note names."
        />
        <DrillCard
          header={
            <Fretboard
              notes={[
                { string: 6, fret: 5, label: "A" },
                { string: 5, fret: 7, label: "E" },
              ]}
            />
          }
          title="Fretboard Intervals"
          url={urls.fretboardInterval}
          category={["Guitar", "Intervals"]}
          description="Study the interval patterns on a fretboard until they become a second nature to you."
        />
      </SingleRow>
      <FeedbackForm />
    </>
  );
}
